import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FlightViewTypes } from 'constants/flight'
import StandaloneFlightStateContext from 'contexts/Flights/StandaloneFlights/standaloneFlightsStateContext'

type SingleLegPriceCaptionTripType = 'oneWay' | 'return' | 'twoOneWays' | 'multiCity'

function getSingleLegPriceCaptionTripType(
  fareType: App.JourneyV2['fareType'],
  viewType: string | undefined,
  isMultiCity?: boolean,
): SingleLegPriceCaptionTripType {
  if (
    isMultiCity ||
    viewType === FlightViewTypes.MULTI_CITY ||
    fareType === FlightViewTypes.MULTI_CITY
  ) {
    return 'multiCity'
  }

  // Two-One-Ways (e.g. domestic AU) sells each leg as its own independent one-way fare -
  // there's no real paired return fare, so this must not share the 'return' caption below.
  // TWO_ONE_WAYS_AND_RETURN is different: it's a genuine paired return fare that's only
  // modelled as two flight items at checkout, so it keeps the 'return' caption. Only the
  // search-wide `viewType` is checked here (matching the pre-existing `isReturnSearch`
  // condition this replaces) - a journey's own `fareType` is never `TwoOneWays` in a way
  // that should affect this caption on its own.
  if (viewType === FlightViewTypes.TWO_ONE_WAYS) {
    return 'twoOneWays'
  }

  const isReturnSearch = viewType === FlightViewTypes.RETURN ||
    viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ||
    fareType === FlightViewTypes.RETURN

  if (isReturnSearch) {
    return 'return'
  }

  return 'oneWay'
}

interface Options {
  fareType: App.JourneyV2['fareType']
  isMultiCity?: boolean
  isRelativePrice: boolean
  isStandalone: boolean
  /**
   * Whether the non-standalone (all-travellers) case is genuinely the bundled hotel + flight
   * flow, as distinct from `isStandalone` being false for a standalone mixed-traveller search.
   * Selects the all-travellers caption: only the bundled flow shows "Hotel and return flight
   * total" - a standalone mixed-traveller search has no hotel and must keep the generic
   * "For all travellers" caption. Defaults to `!isStandalone` for callers where the two cases
   * aren't distinguished.
   */
  isBundledFlow?: boolean
}

function useSingleLegFlightTilePriceCaption({
  fareType,
  isMultiCity,
  isRelativePrice,
  isStandalone,
  isBundledFlow = !isStandalone,
}: Options): string | undefined {
  const intl = useIntl()
  const { viewType, isMixedTravellerSearch } = useContext(StandaloneFlightStateContext)
  const tripType = getSingleLegPriceCaptionTripType(fareType, viewType, isMultiCity)

  return useMemo(() => {
    if (isStandalone && !isRelativePrice && isMixedTravellerSearch) {
      return intl.formatMessage({
        id: 'NfM1-w_GqHGB1iUMbNuZ',
        defaultMessage: 'For all travellers',
        description: 'Caption indicating the price is for all travellers',
      })
    }

    if (isStandalone && !isRelativePrice) {
      if (tripType === 'oneWay') {
        return intl.formatMessage({
          id: 'YS7_HKoA7zvrdkIplbe4',
          defaultMessage: 'One-way per adult',
          description: 'Price caption under a one-way standalone flight tile fare for the per adult amount',
        })
      }

      if (tripType === 'multiCity') {
        return intl.formatMessage({
          id: 'mzKvXsl2UmWPhrFlaocG',
          defaultMessage: 'Multi-city trip per adult',
          description: 'Price caption under a multi-city standalone flight tile fare for the per adult amount',
        })
      }

      if (tripType === 'twoOneWays') {
        return intl.formatMessage({
          id: 'SF1c4uS6a1gaPEDtoKdm',
          defaultMessage: 'Per adult',
          description: 'Tab label for per-adult price filter on flight search',
        })
      }

      return intl.formatMessage({
        id: 'faHtgeX6p6QBclbutc4A',
        defaultMessage: 'Return per adult',
        description: 'Price caption under a return standalone flight tile fare for the per adult amount',
      })
    }

    if (!isStandalone && isBundledFlow) {
      if (fareType === FlightViewTypes.ONE_WAY) {
        return intl.formatMessage({
          id: 'KA1NRIdCR6gkRV0nLpbI',
          defaultMessage: 'Hotel and selected flights total',
          description: 'Price includes the hotel and flights selected so far in a one-way charter bundle',
        })
      }

      return intl.formatMessage({
        id: 'lyGoDh2tSyCxTve2_7G5',
        defaultMessage: 'Hotel and return flight total',
        description: 'Caption indicating the bundled price covers the hotel and return flight total for all travellers',
      })
    }

    if (!isStandalone) {
      return intl.formatMessage({
        id: 'NfM1-w_GqHGB1iUMbNuZ',
        defaultMessage: 'For all travellers',
        description: 'Caption indicating the price is for all travellers',
      })
    }

    return undefined
  }, [intl, isRelativePrice, isStandalone, isBundledFlow, isMixedTravellerSearch, tripType, fareType])
}

export default useSingleLegFlightTilePriceCaption
